import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  orderBy,
  where,
  Timestamp,
} from "firebase/firestore";
import { Post } from "./connections";
import { getUserPosts } from "./postService";
/**
 * Interface for feed data
 */
export interface FeedData {
  feedList: string[]; // Array of post references in format "authorId-postId"
  updatedAt: Timestamp;
}

/**
 * Interface for a feed entry with post data
 */
export interface FeedEntry {
  postId: string;
  authorId: string;
  post: Post;
}

/**
 * Get a user's feed data
 * @param userId The ID of the user whose feed to retrieve
 * @returns FeedData or null if no feed exists
 */
export const getUserFeedData = async (
  userId: string,
): Promise<FeedData | null> => {
  try {
    const feedRef = doc(db, "feeds", userId);
    const feedDoc = await getDoc(feedRef);

    if (!feedDoc.exists()) {
      return null;
    }

    return feedDoc.data() as FeedData;
  } catch (error) {
    console.error("Error getting user feed data:", error);
    throw error;
  }
};

/**
 * Get a user's feed with full post data
 * @param userId The ID of the user whose feed to retrieve
 * @returns Array of FeedEntry objects with post data
 */
export const getUserFeedWithPosts = async (
  userId: string,
): Promise<FeedEntry[]> => {
  try {
    const feedData = await getUserFeedData(userId);

    if (!feedData?.feedList?.length) return [];

    const feedEntries: FeedEntry[] = [];

    for (const feedEntry of feedData.feedList) {
      try {
        const [authorId, postId] = feedEntry.split("-");
        if (!authorId || !postId) continue;

        // ✅ Reuse the new getUserPosts function
        const posts = await getUserPosts(authorId, postId);
        if (posts.length === 0) continue;

        const postData = posts[0]; // single post

        // ✅ Privacy filter: Only show public posts OR posts from self
        if (postData.isPrivate === true && authorId !== userId) {
          continue; // Skip private posts from other users
        }

        feedEntries.push({
          postId,
          authorId,
          post: postData,
        });
      } catch (error) {
        console.error(
          `Error fetching post for feed entry ${feedEntry}:`,
          error,
        );
      }
    }

    // Sort by timestamp
    feedEntries.sort((a, b) => {
      const timestampA = a.post.postTimestamp?.toDate?.() || new Date(0);
      const timestampB = b.post.postTimestamp?.toDate?.() || new Date(0);
      return timestampB.getTime() - timestampA.getTime();
    });

    return feedEntries;
  } catch (error) {
    console.error("Error getting user feed with posts:", error);
    throw error;
  }
};

/**
 * Get a user's feed with pagination (OPTIMIZED - parallel fetching)
 * @param userId The ID of the user whose feed to retrieve
 * @param limit Maximum number of posts to return
 * @param lastPostTimestamp Timestamp of the last post for pagination
 * @returns Array of FeedEntry objects with post data
 */
export const getUserFeedPaginated = async (
  userId: string,
  limit: number = 20,
  lastPostTimestamp?: Date,
): Promise<FeedEntry[]> => {
  try {
    // console.log(
    //   `📡 [Feed] Starting feed fetch for user ${userId.slice(-4)}, limit: ${limit}`,
    // );
    const startTime = performance.now();

    const feedData = await getUserFeedData(userId);
    // console.log(
    //   `📡 [Feed] Got feed data in ${(performance.now() - startTime).toFixed(0)}ms, ${feedData?.feedList?.length || 0} entries`,
    // );

    if (!feedData?.feedList?.length) return [];

    // OPTIMIZATION 1: Fetch ALL timestamps in parallel instead of sequentially
    const timestampStartTime = performance.now();
    const timestampPromises = feedData.feedList.map(async (feedEntry) => {
      try {
        const [authorId, postId] = feedEntry.split("-");
        if (!authorId || !postId) return null;

        const postRef = doc(db, "posts", authorId, "postsList", postId);
        const postDoc = await getDoc(postRef);

        if (postDoc.exists()) {
          const postData = postDoc.data();
          const timestamp = postData.postTimestamp?.toDate?.() || new Date(0);
          return { feedEntry, authorId, postId, timestamp };
        }
        return null;
      } catch (error) {
        console.error(`Error fetching post timestamp for ${feedEntry}:`, error);
        return null;
      }
    });

    const allEntriesRaw = await Promise.all(timestampPromises);
    const allEntries = allEntriesRaw.filter(
      (entry): entry is NonNullable<typeof entry> => entry !== null,
    );
    // console.log(
    //   `📡 [Feed] Fetched ${allEntries.length} timestamps in ${(performance.now() - timestampStartTime).toFixed(0)}ms`,
    // );

    // Sort all entries by timestamp (newest first)
    allEntries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Filter and limit based on pagination
    const entriesToFetch = allEntries
      .filter(
        (entry) => !lastPostTimestamp || entry.timestamp < lastPostTimestamp,
      )
      .slice(0, limit);

    // OPTIMIZATION 2: Fetch full post data in parallel instead of sequentially
    const postFetchStartTime = performance.now();
    const postPromises = entriesToFetch.map(async (entry) => {
      try {
        const posts = await getUserPosts(entry.authorId, entry.postId);
        if (posts.length > 0) {
          const postData = posts[0];

          // ✅ Privacy filter: Only show public posts OR posts from self
          if (postData.isPrivate === true && entry.authorId !== userId) {
            return null; // Skip private posts from other users
          }

          return {
            postId: entry.postId,
            authorId: entry.authorId,
            post: postData,
          } as FeedEntry;
        }
        return null;
      } catch (error) {
        console.error(
          `Error fetching full post data for ${entry.feedEntry}:`,
          error,
        );
        return null;
      }
    });

    const feedEntriesRaw = await Promise.all(postPromises);
    const feedEntries = feedEntriesRaw.filter(
      (entry): entry is FeedEntry => entry !== null,
    );

    // console.log(
    //   `📡 [Feed] Fetched ${feedEntries.length} full posts in ${(performance.now() - postFetchStartTime).toFixed(0)}ms`,
    // );
    // console.log(
    //   `📡 [Feed] Total feed fetch time: ${(performance.now() - startTime).toFixed(0)}ms`,
    // );

    return feedEntries;
  } catch (error) {
    console.error("Error getting user feed with pagination:", error);
    throw error;
  }
};

/**
 * Clear a user's feed (useful for testing or cleanup)
 * @param userId The ID of the user whose feed to clear
 */
export const clearUserFeed = async (userId: string): Promise<void> => {
  try {
    const feedRef = doc(db, "feeds", userId);
    await deleteDoc(feedRef);
    console.log(`Cleared feed for user ${userId}`);
  } catch (error) {
    console.error(`Error clearing feed for user ${userId}:`, error);
    throw error;
  }
};
